
// src/views/UsersManager.jsx
import React, { useState, useEffect } from 'react';
import { client } from '../utils/api';
import DataTable from '../components/DataTable';

const CONTROLLER = 'usersController.php';

const columns = [
  { key: 'id', label: 'ID' },
  { key: 'fullName', label: 'Full Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'city', label: 'City' },
  { key: 'createdAt', label: 'Created At' },
];

const initialFormState = {
  fullName: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
};

export default function UsersManager() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await client.get(CONTROLLER);
      setUsers(res.users || []);
    } catch (err) {
      setError('Failed to fetch users: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await client.put(CONTROLLER, editingId, form);
      } else {
        await client.post(CONTROLLER, form);
      }
      setForm(initialFormState);
      setEditingId(null);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setForm({
      fullName: user.fullName || '',
      email: user.email || '',
      phone: user.phone || '',
      addressLine1: user.addressLine1 || '',
      addressLine2: user.addressLine2 || '',
      city: user.city || '',
      state: user.state || '',
      postalCode: user.postalCode || '',
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await client.delete(CONTROLLER, id);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-view-wrapper">
      {/* 1. Header Row Area */}
      <div className="admin-view-header">
        <h2 className="admin-title">Users Management</h2>
        {error && <div className="admin-error">{error}</div>}
      </div>

      {/* 2. Form Area */}
      <form onSubmit={handleSubmit} className="admin-form-container grid-2-col">
        <div className="form-group">
          <label>Full Name *</label>
          <input
            type="text"
            placeholder="e.g. Alexandre Ivanov"
            required
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Email *</label>
          <input
            type="email"
            placeholder="e.g. alex@example.ch"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Phone</label>
          <input
            type="text"
            placeholder="e.g. +41 21 000 00 00"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Address Line 1</label>
          <input
            type="text"
            placeholder="e.g. Rue de Bourg 12"
            value={form.addressLine1}
            onChange={(e) => setForm({ ...form, addressLine1: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Address Line 2</label>
          <input
            type="text"
            placeholder="e.g. Apt / Suite 4B"
            value={form.addressLine2}
            onChange={(e) => setForm({ ...form, addressLine2: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            placeholder="e.g. Lausanne"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>State / Canton</label>
          <input
            type="text"
            placeholder="e.g. Vaud"
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Postal Code</label>
          <input
            type="text"
            placeholder="e.g. 1003"
            value={form.postalCode}
            onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
          />
        </div>

        <div
          className="btn-actions-container"
          style={{ gridColumn: 'span 2', justifyContent: 'flex-end', marginTop: '8px' }}
        >
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(initialFormState);
              }}
              className="btn btn-cancel"
            >
              Cancel
            </button>
          )}
          <button type="submit" className="btn btn-submit-members">
            {editingId ? 'Update User' : 'Add User'}
          </button>
        </div>
      </form>

      {/* 3. Data Entry Table */}
      <div className="admin-table-section">
        <DataTable columns={columns} data={users} onEdit={handleEdit} onDelete={handleDelete} />
      </div>
    </div>
  );
}